import { useCallback, useEffect, useRef, useState } from 'react'

import { useIntl } from 'react-intl'
import { Required } from 'utility-types'

import useEffectAfterMount from 'app/hooks/useEffectAfterMount'
import { isOfType } from 'app/utils/helpers/type.helpers'

import {
  FileModel,
  FileValue,
  UploadFileProps,
  UploadValue
} from './UploadField.types'
import {
  allSettled,
  generateId,
  getUrlFromFile,
  isEmptyInitialFiles,
  transformFilesToValue,
  transformValueToFiles,
  uploadFieldValidator
} from './UploadField.utils'

export type UseUploadField = {
  maxSize: number
  value: UploadValue
  onChange: (value: UploadValue) => void
  uploadFile: (file: UploadFileProps) => Promise<any>
  multiple?: boolean
  onSuccess?: () => void
  onError?: () => void
  onProcessing?: (processing: boolean) => void
}

const useUploadField = ({
  maxSize,
  value,
  onChange,
  uploadFile,
  multiple = false,
  onSuccess,
  onError,
  onProcessing
}: UseUploadField) => {
  const intl = useIntl()
  const initialized = useRef(false)
  const filesRef = useRef<FileValue>(null)
  const [isProcessing, setProcessing] = useState<boolean>(false)
  const [files, setFiles] = useState<FileValue>(() => {
    const initialState = transformValueToFiles(value, multiple)
    initialized.current = isEmptyInitialFiles(initialState)

    return initialState
  })

  filesRef.current = files

  const isFilesArray = isOfType.array(files)
  const isValueArray = isOfType.array(value)
  const isEmptyFile = isFilesArray ? !files?.length : !files
  const isEmptyValue = isValueArray ? !value.length : !value
  const isMultiple = isFilesArray && multiple
  const canInitialize =
    isEmptyFile && !isEmptyValue && !initialized.current && !!value

  // initialize
  useEffect(() => {
    if (canInitialize) {
      initialized.current = true
      setFiles(transformValueToFiles(value, multiple))
    }
  }, [value, canInitialize])

  // Update form
  useEffect(() => {
    if (!isProcessing) {
      onChange(transformFilesToValue(files, value))
    }
  }, [isProcessing, files])

  useEffectAfterMount(() => {
    if (onProcessing) onProcessing(isProcessing)
  }, [isProcessing])

  const uploadFiles = useCallback(
    async (acceptedFiles: Required<Partial<FileModel>, 'file'>[]) => {
      try {
        const validFiles = acceptedFiles.filter(({ file }) => {
          const error = uploadFieldValidator({
            size: file.size,
            maxSize,
            formatMessage: intl.formatMessage
          })

          return !error
        })

        if (!validFiles.length) return

        initialized.current = true
        setProcessing(true)

        // upload multiple files
        if (multiple && isFilesArray) {
          const values: FileModel[] = [...(filesRef.current as FileModel[])]

          const newChunk = validFiles.map(({ file, ...rest }) => {
            return {
              id: generateId(),
              file,
              loading: true,
              name: file.name,
              url: getUrlFromFile(file),
              size: file?.size || 0,
              ...rest
            }
          })

          setFiles([...values, ...newChunk])

          const uploadFileCallback = async (acceptedFile: FileModel) => {
            try {
              if (!acceptedFile.file) return

              const payload = await uploadFile({
                file: acceptedFile.file,
                url: acceptedFile.url
              })

              setFiles((state) => {
                if (isOfType.array(state)) {
                  return state.map((file) => {
                    if (String(file.id) === String(acceptedFile.id)) {
                      return {
                        ...acceptedFile,
                        loading: false,
                        loaded: true,
                        payload
                      }
                    }

                    return file
                  })
                }

                return state
              })
            } catch (error) {
              console.error(error)

              setFiles((state) => {
                if (Array.isArray(state)) {
                  return state.filter(
                    (file) => String(file.id) !== String(acceptedFile.id)
                  )
                }

                return state
              })
            }
          }

          const promises = newChunk.map((file) => uploadFileCallback(file))

          allSettled(promises).then(() => {
            setProcessing(false)
          })
        } else if (acceptedFiles.length > 0) {
          // upload file
          const acceptedFile = acceptedFiles[0]

          if (!acceptedFile) return null

          const file = acceptedFile?.file

          const newFile = {
            id: generateId(),
            loading: true,
            name: file?.name || '',
            size: file?.size || 0,
            url: getUrlFromFile(file),
            ...acceptedFile
          }

          setFiles(() => ({
            ...newFile
          }))

          try {
            const payload = await uploadFile({ file, url: acceptedFile.url })

            setFiles((prev) => ({
              ...(prev as FileModel),
              loading: false,
              loaded: true,
              payload
            }))
          } catch (error) {
            console.error(error)

            setFiles(null)
          } finally {
            setProcessing(false)
          }
        }

        if (onSuccess) onSuccess()
      } catch (error) {
        if (onError) onError()
        console.error(error)

        setFiles((state) => {
          if (isOfType.array(state) && state) {
            return state.map((file) => ({ ...file, loading: false }))
          }

          return state
        })
      }
    },
    [multiple && isFilesArray, maxSize]
  )

  const deleteFile = (id: string): void => {
    if (multiple) {
      setFiles((state) => {
        if (isOfType.array(state)) {
          return state.filter((file) => String(file.id) !== String(id))
        }

        return state
      })
    } else {
      setFiles(null)
    }
  }

  const changePosition = (data: FileModel[]) => {
    if (isMultiple && data.length === files.length) {
      setFiles(data)
    }
  }

  return {
    disabled: isProcessing,
    isProcessing,
    files,
    uploadFiles,
    deleteFile,
    changePosition
  }
}

export default useUploadField
